// Operationer på Atom-träd: hämta lista vid path, infoga, ta bort, hitta path.
import type { Atom, AtomId, Cursor } from "./types";
import { newId } from "./types";

/** Returnerar referensen till listan vid `path` (mutate-säker — kallaren
 *  bör skriva tillbaka via setRoot). */
export function listAt(root: Atom[], path: AtomId[]): Atom[] | null {
  let list: Atom[] = root;
  for (const containerId of path) {
    const next = findChildList(list, containerId);
    if (!next) return null;
    list = next;
  }
  return list;
}

function findChildList(list: Atom[], containerId: string): Atom[] | null {
  for (const a of list) {
    if (a.id !== containerId) continue;
    switch (a.kind) {
      case "paren":
      case "abs":
      case "sqrt":
        return a.inner;
      case "nthroot":
      case "frac":
      case "sup":
      case "logbase":
      case "fn":
        // För dessa returnerar vi första barn-listan; vi använder
        // `cursor.path` med container-id + ev. *suffix*-marker. För enkelhet
        // i denna implementation hanterar vi container-listor med dedikerad
        // prefix/suffix:
        return null;
    }
  }
  return null;
}

/** Sätt cursor till slutet av rotlistan. */
export function cursorAtEnd(root: Atom[]): Cursor {
  return { path: [], index: root.length };
}

/** Klona ett atom-träd djupt och behåll (eller skapa nya) id:n. */
function cloneAtom(a: Atom, freshIds = false): Atom {
  const id = freshIds ? newId() : a.id;
  switch (a.kind) {
    case "num":
    case "op":
    case "const":
    case "comma":
      return { ...a, id };
    case "paren":
    case "abs":
    case "sqrt":
      return { ...a, id, inner: a.inner.map((c) => cloneAtom(c, freshIds)) };
    case "nthroot":
      return {
        ...a,
        id,
        degree: a.degree.map((c) => cloneAtom(c, freshIds)),
        inner: a.inner.map((c) => cloneAtom(c, freshIds)),
      };
    case "frac":
      return {
        ...a,
        id,
        num: a.num.map((c) => cloneAtom(c, freshIds)),
        den: a.den.map((c) => cloneAtom(c, freshIds)),
      };
    case "sup":
      return {
        ...a,
        id,
        base: a.base.map((c) => cloneAtom(c, freshIds)),
        exp: a.exp.map((c) => cloneAtom(c, freshIds)),
      };
    case "fn":
      return { ...a, id, arg: a.arg.map((c) => cloneAtom(c, freshIds)) };
    case "logbase":
      return {
        ...a,
        id,
        base: a.base.map((c) => cloneAtom(c, freshIds)),
        arg: a.arg.map((c) => cloneAtom(c, freshIds)),
      };
    case "fact":
      return { ...a, id, inner: a.inner.map((c) => cloneAtom(c, freshIds)) };
    case "binom":
      return {
        ...a,
        id,
        n: a.n.map((c) => cloneAtom(c, freshIds)),
        r: a.r.map((c) => cloneAtom(c, freshIds)),
      };
  }
}

export function cloneTree(root: Atom[]): Atom[] {
  return root.map((a) => cloneAtom(a));
}

/** Path-system v2: cursor.path är en sekvens av "containerId:slot" där slot
 *  identifierar vilken barn-lista (för multi-list-noder). Format: "<id>" för
 *  single-list (paren/abs/sqrt), "<id>:N" för degree/num/base/arg-lista,
 *  "<id>:I" för inner/den/exp-lista. */

const SLOT_FIRST = ":0"; // degree, num, base, arg
const SLOT_SECOND = ":1"; // inner, den, exp

export function pathSeg(id: string, slot: 0 | 1 | null = null): string {
  if (slot === null) return id;
  return `${id}${slot === 0 ? SLOT_FIRST : SLOT_SECOND}`;
}

function parseSeg(seg: string): { id: string; slot: 0 | 1 | null } {
  if (seg.endsWith(SLOT_FIRST)) return { id: seg.slice(0, -2), slot: 0 };
  if (seg.endsWith(SLOT_SECOND)) return { id: seg.slice(0, -2), slot: 1 };
  return { id: seg, slot: null };
}

function getChildList(a: Atom, slot: 0 | 1 | null): Atom[] | null {
  switch (a.kind) {
    case "paren":
    case "abs":
    case "sqrt":
      return slot === null ? a.inner : null;
    case "nthroot":
      return slot === 0 ? a.degree : slot === 1 ? a.inner : null;
    case "frac":
      return slot === 0 ? a.num : slot === 1 ? a.den : null;
    case "sup":
      return slot === 0 ? a.base : slot === 1 ? a.exp : null;
    case "fn":
      return slot === 0 ? a.arg : null;
    case "logbase":
      return slot === 0 ? a.base : slot === 1 ? a.arg : null;
    case "fact":
      return slot === null ? a.inner : null;
    case "binom":
      return slot === 0 ? a.n : slot === 1 ? a.r : null;
    default:
      return null;
  }
}

export function getListAtPath(root: Atom[], path: string[]): Atom[] | null {
  let list: Atom[] = root;
  for (const seg of path) {
    const { id, slot } = parseSeg(seg);
    const node = list.find((a) => a.id === id);
    if (!node) return null;
    const next = getChildList(node, slot);
    if (!next) return null;
    list = next;
  }
  return list;
}

/** Returnerar nytt root-träd med `mutator` applicerad på listan vid path. */
export function withListAtPath(
  root: Atom[],
  path: string[],
  mutator: (list: Atom[]) => Atom[],
): Atom[] {
  if (path.length === 0) return mutator(root);
  const [first, ...rest] = path;
  const { id, slot } = parseSeg(first);
  return root.map((a) => {
    if (a.id !== id) return a;
    return updateChildList(a, slot, (childList) =>
      withListAtPath(childList, rest, mutator),
    );
  });
}

function updateChildList(
  a: Atom,
  slot: 0 | 1 | null,
  mutator: (list: Atom[]) => Atom[],
): Atom {
  switch (a.kind) {
    case "paren":
    case "abs":
    case "sqrt":
      if (slot !== null) return a;
      return { ...a, inner: mutator(a.inner) };
    case "nthroot":
      return slot === 0
        ? { ...a, degree: mutator(a.degree) }
        : slot === 1
          ? { ...a, inner: mutator(a.inner) }
          : a;
    case "frac":
      return slot === 0
        ? { ...a, num: mutator(a.num) }
        : slot === 1
          ? { ...a, den: mutator(a.den) }
          : a;
    case "sup":
      return slot === 0
        ? { ...a, base: mutator(a.base) }
        : slot === 1
          ? { ...a, exp: mutator(a.exp) }
          : a;
    case "fn":
      return slot === 0 ? { ...a, arg: mutator(a.arg) } : a;
    case "logbase":
      return slot === 0
        ? { ...a, base: mutator(a.base) }
        : slot === 1
          ? { ...a, arg: mutator(a.arg) }
          : a;
    case "fact":
      return slot === null ? { ...a, inner: mutator(a.inner) } : a;
    case "binom":
      return slot === 0
        ? { ...a, n: mutator(a.n) }
        : slot === 1
          ? { ...a, r: mutator(a.r) }
          : a;
    default:
      return a;
  }
}

/** Infoga en atom vid cursor och flytta cursor förbi den. */
export function insertAt(
  root: Atom[],
  cursor: Cursor,
  atom: Atom,
): { root: Atom[]; cursor: Cursor } {
  const newRoot = withListAtPath(root, cursor.path, (list) => {
    const next = list.slice();
    next.splice(cursor.index, 0, atom);
    return next;
  });
  return { root: newRoot, cursor: { ...cursor, index: cursor.index + 1 } };
}

/** Infoga en grupp (sup/paren/sqrt/frac/fn) och placera cursor inne i gruppens
 *  primärslot. */
export function insertGroup(
  root: Atom[],
  cursor: Cursor,
  group: Atom,
  slot: 0 | 1 | null = null,
): { root: Atom[]; cursor: Cursor } {
  const inserted = insertAt(root, cursor, group);
  // Flytta cursor IN i gruppens slot
  const newPath = [...cursor.path, pathSeg(group.id, slot)];
  return { root: inserted.root, cursor: { path: newPath, index: 0 } };
}

/** Backspace:
 *  - Vid index 0 (tom slot eller början av root): inget händer.
 *  - Om atomen före cursor är ett num med fler än ett tecken: korta strängen
 *    med ett tecken (radera siffra-för-siffra för flersiffriga tal).
 *  - Annars: ta bort hela atomen vid index-1.
 */
export function backspace(
  root: Atom[],
  cursor: Cursor,
): { root: Atom[]; cursor: Cursor } {
  if (cursor.index === 0) {
    return { root, cursor };
  }
  const list = getListAtPath(root, cursor.path);
  if (list) {
    const prev = list[cursor.index - 1];
    if (prev && prev.kind === "num" && prev.v.length > 1) {
      const newRoot = withListAtPath(root, cursor.path, (l) => {
        const next = l.slice();
        const old = next[cursor.index - 1];
        if (old.kind === "num") {
          next[cursor.index - 1] = { ...old, v: old.v.slice(0, -1) };
        }
        return next;
      });
      return { root: newRoot, cursor };
    }
  }
  const newRoot = withListAtPath(root, cursor.path, (list) => {
    const next = list.slice();
    next.splice(cursor.index - 1, 1);
    return next;
  });
  return { root: newRoot, cursor: { ...cursor, index: cursor.index - 1 } };
}

/** Lägg till digit (0-9) — slå ihop med senaste num om möjligt. */
export function insertDigit(
  root: Atom[],
  cursor: Cursor,
  digit: string,
): { root: Atom[]; cursor: Cursor } {
  const list = getListAtPath(root, cursor.path);
  if (list && cursor.index > 0) {
    const prev = list[cursor.index - 1];
    if (prev.kind === "num") {
      // Slå ihop digit till prev.v
      const newRoot = withListAtPath(root, cursor.path, (l) => {
        const next = l.slice();
        const old = next[cursor.index - 1];
        if (old.kind === "num") {
          next[cursor.index - 1] = { ...old, v: old.v + digit };
        }
        return next;
      });
      return { root: newRoot, cursor };
    }
  }
  return insertAt(root, cursor, { id: newId(), kind: "num", v: digit });
}

/** Move cursor one step left (linear traversal of the expression tree).
 *  - If we're at index > 0 and the previous atom is a complex container
 *    (paren/sqrt/frac/sup/abs/nthroot/fn/logbase), descend INTO its rightmost
 *    sub-slot at the end position.
 *  - Else if at index > 0, just decrement.
 *  - Else (at index 0): pop OUT of the current slot to the position *before*
 *    the container in the parent list. If there's a previous sub-slot in the
 *    same container (sup.exp → sup.base, frac.den → frac.num, nthroot.inner →
 *    nthroot.degree, logbase.arg → logbase.base), descend into it at end first.
 */
export function moveLeft(root: Atom[], cursor: Cursor): Cursor {
  const list = getListAtPath(root, cursor.path);
  if (!list) return cursor;
  if (cursor.index > 0) {
    const prev = list[cursor.index - 1];
    const target = enterFromRight(prev);
    if (target) {
      return {
        path: [...cursor.path, pathSeg(prev.id, target.slot)],
        index: target.list.length,
      };
    }
    return { ...cursor, index: cursor.index - 1 };
  }
  // Index 0 — pop out, but first check for previous sibling slot in same container
  if (cursor.path.length === 0) return cursor;
  const containerSeg = cursor.path[cursor.path.length - 1];
  const { id: containerId, slot } = parseSeg(containerSeg);
  const parentPath = cursor.path.slice(0, -1);
  const parentList = getListAtPath(root, parentPath);
  if (!parentList) return cursor;
  const container = parentList.find((a) => a.id === containerId);
  if (!container) return cursor;

  // If we're in slot 1 (exp/den/inner/arg), step LEFT into slot 0 (base/num/degree/base) at end.
  if (slot === 1) {
    const slot0 = getChildList(container, 0);
    if (slot0) {
      return {
        path: [...parentPath, pathSeg(containerId, 0)],
        index: slot0.length,
      };
    }
  }
  // Otherwise, exit container — cursor goes BEFORE the container in parent list
  const parentIdx = parentList.findIndex((a) => a.id === containerId);
  if (parentIdx < 0) return cursor;
  return { path: parentPath, index: parentIdx };
}

/** Move cursor one step right.
 *  - If the next atom is a complex container, descend INTO its leftmost
 *    sub-slot at the start position.
 *  - Else if there is a next atom, increment index.
 *  - Else (at end): pop OUT to position *after* the container, but first
 *    check for next sibling slot (sup.base → sup.exp, etc.).
 */
export function moveRight(root: Atom[], cursor: Cursor): Cursor {
  const list = getListAtPath(root, cursor.path);
  if (!list) return cursor;
  if (cursor.index < list.length) {
    const next = list[cursor.index];
    const target = enterFromLeft(next);
    if (target) {
      return { path: [...cursor.path, pathSeg(next.id, target.slot)], index: 0 };
    }
    return { ...cursor, index: cursor.index + 1 };
  }
  // At end of list — pop out / step into next sibling slot
  if (cursor.path.length === 0) return cursor;
  const containerSeg = cursor.path[cursor.path.length - 1];
  const { id: containerId, slot } = parseSeg(containerSeg);
  const parentPath = cursor.path.slice(0, -1);
  const parentList = getListAtPath(root, parentPath);
  if (!parentList) return cursor;
  const container = parentList.find((a) => a.id === containerId);
  if (!container) return cursor;

  if (slot === 0) {
    const slot1 = getChildList(container, 1);
    if (slot1) {
      return {
        path: [...parentPath, pathSeg(containerId, 1)],
        index: 0,
      };
    }
  }
  const parentIdx = parentList.findIndex((a) => a.id === containerId);
  if (parentIdx < 0) return cursor;
  return { path: parentPath, index: parentIdx + 1 };
}

/** Returns the slot to descend into when stepping LEFT past `a` (rightmost slot
 *  if multi-slot). Returns null for atoms without sub-lists. */
function enterFromRight(a: Atom): { slot: 0 | 1 | null; list: Atom[] } | null {
  switch (a.kind) {
    case "paren":
    case "abs":
    case "sqrt":
      return { slot: null, list: a.inner };
    case "nthroot":
      return { slot: 1, list: a.inner };
    case "frac":
      return { slot: 1, list: a.den };
    case "sup":
      return { slot: 1, list: a.exp };
    case "fn":
      return { slot: 0, list: a.arg };
    case "logbase":
      return { slot: 1, list: a.arg };
    case "fact":
      return { slot: null, list: a.inner };
    case "binom":
      return { slot: 1, list: a.r };
    default:
      return null;
  }
}

function enterFromLeft(a: Atom): { slot: 0 | 1 | null } | null {
  switch (a.kind) {
    case "paren":
    case "abs":
    case "sqrt":
      return { slot: null };
    case "nthroot":
    case "frac":
    case "sup":
    case "logbase":
      return { slot: 0 };
    case "fn":
      return { slot: 0 };
    case "fact":
      return { slot: null };
    case "binom":
      return { slot: 0 };
    default:
      return null;
  }
}

/** Decimal: lägg till "." på senaste num om den inte redan har en. */
export function insertDecimal(
  root: Atom[],
  cursor: Cursor,
): { root: Atom[]; cursor: Cursor } {
  const list = getListAtPath(root, cursor.path);
  if (list && cursor.index > 0) {
    const prev = list[cursor.index - 1];
    if (prev.kind === "num" && !prev.v.includes(".")) {
      const newRoot = withListAtPath(root, cursor.path, (l) => {
        const next = l.slice();
        const old = next[cursor.index - 1];
        if (old.kind === "num") {
          next[cursor.index - 1] = { ...old, v: old.v + "." };
        }
        return next;
      });
      return { root: newRoot, cursor };
    }
  }
  // Om cursor inte är efter num, sätt "0."
  return insertAt(root, cursor, { id: newId(), kind: "num", v: "0." });
}
